import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const EVENT_LABELS = {
  item_created: 'Item criado',
  item_updated: 'Item atualizado',
  item_deleted: 'Item excluído',
  automation_action: 'Automação executada',
  test: 'Teste'
};

const ALL_EVENTS = Object.keys(EVENT_LABELS);

export default function BoardWebhooks({ board, api }) {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', events: ['item_created', 'item_updated'], active: true, secret: '' });

  useEffect(() => {
    if (board) fetchWebhooks();
  }, [board]);

  const fetchWebhooks = async () => {
    try {
      const res = await api.get(`/projects/boards/${board.id}/webhooks`);
      setWebhooks(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingWebhook(null);
    setForm({ name: '', url: '', events: ['item_created', 'item_updated'], active: true, secret: '' });
    setShowModal(true);
  };

  const openEdit = (wh) => {
    setEditingWebhook(wh);
    setForm({ name: wh.name, url: wh.url, events: wh.events, active: wh.active, secret: wh.secret || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.url) { toast.error('Nome e URL são obrigatórios'); return; }
    try {
      const payload = { ...form, secret: form.secret || null };
      if (editingWebhook) {
        await api.put(`/projects/webhooks/${editingWebhook.id}`, payload);
        toast.success('Webhook atualizado!');
      } else {
        await api.post(`/projects/boards/${board.id}/webhooks`, payload);
        toast.success('Webhook criado!');
      }
      setShowModal(false);
      fetchWebhooks();
    } catch (e) {
      toast.error('Erro ao salvar webhook');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir webhook?')) return;
    try {
      await api.delete(`/projects/webhooks/${id}`);
      setWebhooks(webhooks.filter(w => w.id !== id));
      toast.success('Webhook excluído');
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const handleToggle = async (wh, active) => {
    try {
      await api.put(`/projects/webhooks/${wh.id}`, { active });
      setWebhooks(webhooks.map(w => w.id === wh.id ? { ...w, active } : w));
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleTest = async (id) => {
    try {
      await api.post(`/projects/webhooks/${id}/test`);
      toast.success('Payload de teste disparado!');
    } catch (e) {
      toast.error('Erro ao testar webhook');
    }
  };

  const toggleEvent = (evt) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(evt) ? f.events.filter(e => e !== evt) : [...f.events, evt]
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Webhooks</h2>
          <p className="text-sm text-gray-500">Receba notificações HTTP quando eventos ocorrem neste board</p>
        </div>
        <Button onClick={openCreate}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Novo Webhook
        </Button>
      </div>

      {webhooks.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="text-4xl mb-3">🔗</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum webhook configurado</h3>
            <p className="text-gray-500 text-sm mb-4">Conecte este board a sistemas externos via HTTP</p>
            <Button onClick={openCreate}>Criar Webhook</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {webhooks.map(wh => (
            <Card key={wh.id} className={wh.active ? 'border-green-200' : 'border-gray-200 opacity-70'}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-semibold text-gray-900">{wh.name}</span>
                      <Badge variant={wh.active ? 'default' : 'secondary'} className="text-xs">
                        {wh.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 font-mono truncate">{wh.url}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {wh.events.map(e => (
                        <span key={e} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                          {EVENT_LABELS[e] || e}
                        </span>
                      ))}
                    </div>
                    {wh.last_fired_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Último disparo: {new Date(wh.last_fired_at).toLocaleString('pt-BR')} · {wh.fire_count} disparos
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4 shrink-0">
                    <Switch checked={wh.active} onCheckedChange={(v) => handleToggle(wh, v)} />
                    <Button size="sm" variant="outline" onClick={() => handleTest(wh.id)}>Testar</Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(wh)}>Editar</Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(wh.id)}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingWebhook ? 'Editar Webhook' : 'Novo Webhook'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Nome *</Label>
              <Input className="mt-1" placeholder="Ex: Notificação Slack" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>URL de destino *</Label>
              <Input className="mt-1" placeholder="https://..." value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
            </div>
            <div>
              <Label>Segredo HMAC (opcional)</Label>
              <Input className="mt-1" type="password" placeholder="Para verificar a assinatura" value={form.secret} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-2 block">Eventos</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_EVENTS.filter(e => e !== 'test').map(evt => (
                  <label key={evt} className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" checked={form.events.includes(evt)} onChange={() => toggleEvent(evt)} className="rounded" />
                    <span className="text-sm text-gray-700">{EVENT_LABELS[evt]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
              <Label>Webhook ativo</Label>
            </div>
            <div className="flex space-x-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button onClick={handleSave}>Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
